<template>
  <div
    id="membershipFees"
    class="pa-4"
  >
    <view-page-header all-teams />

    <portal
      key="membershipFeesPortal"
      to="headerFilters"
    >
      <v-row
        align="center"
        dense
      >
        <v-col
          cols="auto"
          class="text-subtitle-1 grey--text pr-2"
        >
          {{ $t('dropdown.select_month.title') }}
        </v-col>
        <v-col cols="auto">
          <v-btn
            icon
            :disabled="selectedIndex === undefined || !membershipPeriods.data[selectedIndex + 1]"
            @click="membershipPeriods.selected = membershipPeriods.data[selectedIndex + 1]"
          >
            <v-icon small>
              fa fa-arrow-left
            </v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-select
            key="selectDateForMembership"
            v-model="membershipPeriods.selected"
            :items="membershipPeriods.data"
            :loading="membershipPeriods.loading"
            solo
            hide-details
            style="width: 400px"
            item-value="id"
            item-text="date"
            return-object
          >
            <template v-slot:item="{ item }">
              <v-list-item-content v-if="item.interval === membershipPlanIntervalMonthly">
                <v-list-item-title>
                  {{ $t('membership.payment_intervals.month') }} {{ $dayjs(item.date).format('MMMM, MM/YYYY') }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-content v-else-if="item.interval === membershipPlanIntervalQuarterly">
                <v-list-item-title>
                  {{ $t('membership.payment_intervals.quarter') }} {{ $dayjs(item.date).format('MM/YYYY') }} - {{ $dayjs(item.date).add(2, 'month').format('MM/YYYY') }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-content v-else-if="item.interval === membershipPlanIntervalHalfYearly">
                <v-list-item-title>
                  {{ $t('membership.payment_intervals.half_of_year') }} {{ $dayjs(item.date).format('MM/YYYY') }} - {{ $dayjs(item.date).add(5, 'month').format('MM/YYYY') }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-content v-else-if="item.interval === membershipPlanIntervalYearly">
                <v-list-item-title>
                  {{ $t('membership.payment_intervals.year') }} {{ $dayjs(item.date).format('YYYY') }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item }">
              <div v-if="item.interval === membershipPlanIntervalMonthly">
                {{ $t('membership.payment_intervals.month') }} {{ $dayjs(item.date).format('MMMM, MM/YYYY') }}
              </div>
              <div v-else-if="item.interval === membershipPlanIntervalQuarterly">
                {{ $t('membership.payment_intervals.quarter') }} {{ $dayjs(item.date).format('MM/YYYY') }} - {{ $dayjs(item.date).add(2, 'month').format('MM/YYYY') }}
              </div>
              <div v-else-if="item.interval === membershipPlanIntervalHalfYearly">
                {{ $t('membership.payment_intervals.half_of_year') }} {{ $dayjs(item.date).format('MM/YYYY') }} - {{ $dayjs(item.date).add(5, 'month').format('MM/YYYY') }}
              </div>
              <div v-else-if="item.interval === membershipPlanIntervalYearly">
                {{ $t('membership.payment_intervals.year') }} {{ $dayjs(item.date).format('YYYY') }}
              </div>
            </template>
          </v-select>
        </v-col>
        <v-col cols="auto">
          <v-btn
            icon
            :disabled="selectedIndex === undefined || !membershipPeriods.data[selectedIndex - 1]"
            @click="membershipPeriods.selected = membershipPeriods.data[selectedIndex - 1]"
          >
            <v-icon small>
              fa fa-arrow-right
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </portal>

    <v-divider />

    <section class="diagnostics__tables mb-6 mt-6">
      <v-card>
        <v-card-title class="pa-6">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('event_details.players') }}
          </div>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="headers"
            :loading="players.loading"
            :items="players.data"
            disable-pagination
            hide-default-footer
            sort-by="last_name"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td>
                  <div class="d-flex align-center">
                    <v-avatar
                      size="32px"
                      class="mr-2"
                      color="grey lighten-2"
                    >
                      <v-img :src="item.photo_url">
                        <template v-slot:placeholder>
                          <v-icon x-small>
                            fal fa-user
                          </v-icon>
                        </template>
                      </v-img>
                    </v-avatar>
                    {{ item.last_name }}, {{ item.first_name }}
                  </div>
                </td>
                <td>
                  {{ item.membership_plan.title }}
                </td>
                <td>
                  <span v-if="item.membership_payment">
                    {{ item.membership_payment.currency }} {{ Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(item.membership_payment.amount) }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td>
                  <span v-if="item.membership_payment && item.membership_payment.payment_due_date">
                    {{ $dayjs(item.membership_payment.payment_due_date).format('ll') }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td>
                  <v-btn
                    :text="!item.membership_payment.is_paid"
                    small
                    :color="!item.membership_payment.is_paid ? 'error' : 'success'"
                    rounded
                    class="text-capitalize"
                    @click="$can('edit-player-membership') ? updatePaidStatus(item.membership_payment) : undefined"
                  >
                    <v-icon
                      left
                      small
                    >
                      {{ !item.membership_payment.is_paid ? 'fa fa-times' : 'fa fa-check' }}
                    </v-icon>
                    {{ !item.membership_payment.is_paid ? $t('status.payment_status.not_paid') : $t('status.payment_status.paid') }}
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </section>
  </div>
</template>

<script>
import { PlayerApiService } from '@/data/services'
import ViewPageHeader from '@/components/ViewPageHeader'
import debounce from 'lodash/debounce'
import { MembershipPlanApiService } from '@/data/services/MembershipPlanApiService'
import i18n from '@/plugins/i18n'
import { MembershipPlanInterval, MembershipPlanIntervals } from '@/data/models/MembershipPlan'

export default {
  name: 'MembershipFeesView',
  components: {
    ViewPageHeader
  },
  data: () => ({
    players: {
      data: [],
      loading: false
    },
    membershipPeriods: {
      data: [],
      selected: undefined,
      loading: false
    }
  }),
  computed: {
    currentTeamId() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM].id
    },
    headers() {
      return [
        { text: i18n.t('table_headers.name'), value: 'last_name', width: '20%' },
        { text: i18n.t('table_headers.membership_fee.plan'), value: 'membership_plan_id', width: '20%' },
        { text: i18n.t('table_headers.membership_fee.amount_to_pay'), value: 'amount', width: '20%' },
        { text: this.$t('table_headers.membership_fee.due_date'), value: 'payment_due_date', width: '20%' },
        { text: i18n.t('status.payment_status.not_paid'), value: 'is_paid' }
      ]
    },
    selectedIndex() {
      if (!this.membershipPeriods.selected) return false

      return this.membershipPeriods.data.findIndex(p => p.id === this.membershipPeriods.selected.id)
    }
  },
  watch: {
    'membershipPeriods.selected': function () {
      this.fetchPlayers()
    },
    currentTeamId: function () {
      this.fetchPlayers()
    }
  },
  created() {
    this.membershipPlanIntervals = MembershipPlanIntervals
    this.membershipPlanIntervalMonthly = MembershipPlanInterval.MONTHLY
    this.membershipPlanIntervalQuarterly = MembershipPlanInterval.QUARTERLY
    this.membershipPlanIntervalHalfYearly = MembershipPlanInterval.HALF_YEARLY
    this.membershipPlanIntervalYearly = MembershipPlanInterval.YEARLY

    this.fetchMembershipPeriods()
  },
  methods: {
    fetchPlayers: debounce(async function () {
      try {
        if (!this.membershipPeriods.selected) return

        this.players.loading = true

        const queryParams = {
          team_id: this.currentTeamId === 'all' ? undefined : this.currentTeamId,
          membership_period_id: this.membershipPeriods.selected.id
        }

        const response = await PlayerApiService.getSimpleList(queryParams)

        this.players.data = response.data
      } catch (e) {
        this.players.data = []
        throw e
      } finally {
        this.players.loading = false
      }
    }, 50),
    async fetchMembershipPeriods() {
      this.membershipPeriods.loading = true

      try {
        const response = await MembershipPlanApiService.getPeriods()

        this.membershipPeriods.data = response.data

        if (response.data.length > 0) {
          this.membershipPeriods.selected = response.data[0]
        }
      } finally {
        this.membershipPeriods.loading = false
      }
    },
    async updatePaidStatus(membershipPayment) {
      const response = await MembershipPlanApiService.updatePaymentStatus(membershipPayment.id, { is_paid: !membershipPayment.is_paid })

      membershipPayment.is_paid = response.data.is_paid

      this.$store.dispatch(this.$storeActions.ALERT, {
        text: this.$t('alert.payment_updated.success'),
        color: 'success'
      })
    }
  }
}
</script>
